import React, { useEffect, useRef, useState } from "react";
import {
  listMySchools,
  listFeatureRequests,
  createFeatureRequest,
  getFeatureRequestFile,
  setFeatureRequestStatus,
  deleteFeatureRequest,
} from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

// "Yêu cầu chức năng": người dùng định nghĩa 1 chức năng mới (tên + mô tả +
// file Excel mẫu) rồi bấm "Gửi yêu cầu" -> lưu lên server. Đội phát triển
// đọc yêu cầu (kèm bản xem trước Excel) để làm chức năng, cập nhật trạng thái.
const STATUS_LABELS = {
  new: { label: "Mới gửi", color: "#2563eb", bg: "#dbeafe" },
  in_progress: { label: "Đang làm", color: "#b45309", bg: "#fef3c7" },
  done: { label: "Đã xong", color: "#047857", bg: "#d1fae5" },
  rejected: { label: "Từ chối", color: "#b91c1c", bg: "#fee2e2" },
};
const MAX_FILE_BYTES = 8 * 1024 * 1024;

const readAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result).split(",")[1] || "");
    fr.onerror = () => reject(new Error("Không đọc được file."));
    fr.readAsDataURL(file);
  });

const fmtTime = (ts) => (ts ? new Date(ts).toLocaleString("vi-VN") : "");
const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(n / 1024)} KB`);

const HappySchoolFeatureRequests = () => {
  const schoolId = getActiveSchoolId();
  const [role, setRole] = useState(null);
  const [list, setList] = useState(null);
  const [error, setError] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState("");
  const [openId, setOpenId] = useState(null);
  const fileInput = useRef(null);
  const isAdmin = role === "owner" || role === "admin";

  const loadList = () => listFeatureRequests(schoolId).then(setList).catch((e) => setError(e.message));
  useEffect(() => {
    if (!schoolId) return;
    listMySchools()
      .then((schools) => { const s = (schools || []).find((x) => x.id === schoolId); setRole(s ? s.role : null); })
      .catch(() => {});
    loadList();
    // eslint-disable-next-line
  }, [schoolId]);

  if (!schoolId) return <p style={{ padding: 20, color: "#6b7280" }}>Chưa chọn trường.</p>;

  const pickFile = (e) => {
    const f = e.target.files && e.target.files[0];
    setError("");
    if (!f) { setFile(null); return; }
    if (!/\.(xlsx|xls|csv)$/i.test(f.name)) { setError("Chỉ nhận file .xlsx, .xls, .csv."); e.target.value = ""; return; }
    if (f.size > MAX_FILE_BYTES) { setError("File quá lớn (tối đa 8MB)."); e.target.value = ""; return; }
    setFile(f);
  };

  const submit = async () => {
    setError(""); setNotice("");
    if (!title.trim()) { setError("Nhập tên chức năng."); return; }
    if (!description.trim()) { setError("Nhập mô tả chức năng."); return; }
    setSubmitting(true);
    try {
      const payload = { title: title.trim(), description: description.trim() };
      if (file) payload.file = { name: file.name, mime: file.type, base64: await readAsBase64(file) };
      const created = await createFeatureRequest(schoolId, payload);
      setTitle(""); setDescription(""); setFile(null);
      if (fileInput.current) fileInput.current.value = "";
      setNotice(`Đã gửi yêu cầu "${created.title}" lên server.`);
      setOpenId(created.id);
      loadList();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const download = async (r) => {
    try {
      const blob = await getFeatureRequestFile(schoolId, r.id);
      const bytes = Uint8Array.from(atob(blob.base64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: blob.mime || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url; a.download = blob.name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
  };

  const changeStatus = async (r, status) => {
    try { await setFeatureRequestStatus(schoolId, r.id, status, r.adminNote); loadList(); }
    catch (e) { setError(e.message); }
  };

  const saveNote = async (r, adminNote) => {
    try { await setFeatureRequestStatus(schoolId, r.id, r.status, adminNote); loadList(); }
    catch (e) { setError(e.message); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Xoá yêu cầu "${r.title}"?`)) return;
    try { await deleteFeatureRequest(schoolId, r.id); loadList(); }
    catch (e) { setError(e.message); }
  };

  return (
    <div style={{ padding: 20, maxWidth: 1100 }}>
      <h2 style={{ margin: "0 0 4px" }}>💡 Yêu cầu chức năng</h2>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Định nghĩa chức năng bạn cần: đặt tên, mô tả cách hoạt động, đính kèm file Excel mẫu (dữ liệu/biểu mẫu đang dùng).
        Bấm "Gửi yêu cầu" để lưu lên server — đội phát triển sẽ dựa vào yêu cầu này để làm chức năng.
      </p>

      <div style={card}>
        <label style={lbl}>Tên chức năng *</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="VD: Quản lý mượn trả sách thư viện"
          style={input}
          maxLength={200}
        />

        <label style={lbl}>Mô tả chức năng *</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={"Mô tả càng rõ càng tốt:\n- Ai dùng chức năng này (admin, GV, HS...)?\n- Cần nhập / xem / xuất những thông tin gì?\n- File Excel đính kèm dùng để làm gì (dữ liệu mẫu, biểu mẫu cần xuất ra...)?\n- Có tính toán / quy tắc đặc biệt nào không?"}
          style={{ ...input, minHeight: 160, resize: "vertical", fontFamily: "inherit" }}
        />

        <label style={lbl}>File Excel mẫu (không bắt buộc, .xlsx/.xls/.csv, tối đa 8MB)</label>
        <input ref={fileInput} type="file" accept=".xlsx,.xls,.csv" onChange={pickFile} style={{ fontSize: 13 }} />
        {file && <span style={{ marginLeft: 8, fontSize: 13, color: "#374151" }}>{file.name} ({fmtSize(file.size)})</span>}

        <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
          <button type="button" onClick={submit} disabled={submitting} style={btn}>
            {submitting ? "Đang gửi..." : "📤 Gửi yêu cầu"}
          </button>
          {notice && <span style={{ color: "#047857", fontSize: 13 }}>{notice}</span>}
        </div>
        {error && <p style={{ color: "#dc2626", margin: "8px 0 0" }}>{error}</p>}
      </div>

      <h3 style={{ margin: "24px 0 8px" }}>{isAdmin ? "Tất cả yêu cầu" : "Yêu cầu của tôi"}</h3>
      {!list ? (
        <p>Đang tải...</p>
      ) : list.length === 0 ? (
        <p style={{ color: "#6b7280" }}>Chưa có yêu cầu nào.</p>
      ) : (
        list.map((r) => (
          <RequestItem
            key={r.id}
            r={r}
            open={openId === r.id}
            onToggle={() => setOpenId(openId === r.id ? null : r.id)}
            isAdmin={isAdmin}
            onDownload={() => download(r)}
            onStatus={(s) => changeStatus(r, s)}
            onSaveNote={(n) => saveNote(r, n)}
            onDelete={() => remove(r)}
          />
        ))
      )}
    </div>
  );
};

function RequestItem({ r, open, onToggle, isAdmin, onDownload, onStatus, onSaveNote, onDelete }) {
  const st = STATUS_LABELS[r.status] || STATUS_LABELS.new;
  const [note, setNote] = useState(r.adminNote || "");
  const [sheetIdx, setSheetIdx] = useState(0);
  useEffect(() => setNote(r.adminNote || ""), [r.adminNote]);
  const sheets = (r.file && r.file.sheets) || [];
  const sheet = sheets[sheetIdx];
  const canDelete = isAdmin || r.status === "new";

  return (
    <div style={{ ...card, padding: 0, marginBottom: 10 }}>
      <div onClick={onToggle} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", cursor: "pointer" }}>
        <span style={{ ...badge, color: st.color, background: st.bg }}>{st.label}</span>
        <strong style={{ flex: 1 }}>{r.title}</strong>
        {r.file && <span title={r.file.name} style={{ fontSize: 12, color: "#6b7280" }}>📎 Excel</span>}
        <span style={{ fontSize: 12, color: "#9ca3af" }}>
          {r.createdByName ? `${r.createdByName} · ` : ""}{fmtTime(r.createdAt)}
        </span>
        <span style={{ color: "#9ca3af" }}>{open ? "▲" : "▼"}</span>
      </div>

      {open && (
        <div style={{ borderTop: "1px solid #e5e7eb", padding: 16 }}>
          <div style={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.5 }}>{r.description}</div>

          {r.adminNote && !isAdmin && (
            <p style={{ background: "#f9fafb", borderLeft: "3px solid #0ea5a3", padding: "8px 12px", fontSize: 13 }}>
              <strong>Phản hồi:</strong> {r.adminNote}
            </p>
          )}

          {r.file && (
            <div style={{ marginTop: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13 }}>📎 <strong>{r.file.name}</strong> ({fmtSize(r.file.size)})</span>
                <button type="button" onClick={onDownload} style={btn2}>Tải file gốc</button>
              </div>
              {sheets.length > 0 && (
                <>
                  <div style={{ display: "flex", gap: 4, margin: "10px 0 6px", flexWrap: "wrap" }}>
                    {sheets.map((s, i) => (
                      <button key={s.name} type="button" onClick={() => setSheetIdx(i)} style={i === sheetIdx ? tabActive : tab}>
                        {s.name} <span style={{ opacity: 0.7 }}>({s.rowCount}×{s.colCount})</span>
                      </button>
                    ))}
                  </div>
                  {sheet && (sheet.preview || []).length > 0 ? (
                    <div style={{ overflow: "auto", maxHeight: 360, border: "1px solid #e5e7eb", borderRadius: 6 }}>
                      <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
                        <tbody>
                          {sheet.preview.map((row, i) => (
                            <tr key={i}>
                              <td style={{ ...td, background: "#f9fafb", color: "#9ca3af" }}>{i + 1}</td>
                              {row.map((c, j) => <td key={j} style={i === 0 ? { ...td, fontWeight: 600, background: "#f9fafb" } : td}>{c}</td>)}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p style={{ fontSize: 13, color: "#6b7280" }}>Sheet trống.</p>
                  )}
                  {sheet && sheet.rowCount > sheet.preview.length && (
                    <p style={{ fontSize: 12, color: "#9ca3af", margin: "4px 0 0" }}>
                      Xem trước {sheet.preview.length}/{sheet.rowCount} dòng — tải file gốc để xem đầy đủ.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
            {isAdmin && (
              <>
                <span style={{ fontSize: 13 }}>Trạng thái:</span>
                <select value={r.status} onChange={(e) => onStatus(e.target.value)} style={{ padding: "5px 8px", borderRadius: 6, border: "1px solid #d1d5db" }}>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ghi chú phản hồi cho người gửi" style={{ ...input, flex: 1, minWidth: 220, marginBottom: 0 }} />
                <button type="button" onClick={() => onSaveNote(note)} disabled={note === (r.adminNote || "")} style={btn2}>Lưu ghi chú</button>
              </>
            )}
            {canDelete && <button type="button" onClick={onDelete} style={{ ...btn2, borderColor: "#dc2626", color: "#dc2626", marginLeft: "auto" }}>Xoá</button>}
          </div>
        </div>
      )}
    </div>
  );
}

const card = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16 };
const lbl = { display: "block", fontSize: 13, fontWeight: 600, margin: "10px 0 4px", color: "#374151" };
const input = { width: "100%", boxSizing: "border-box", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 14, marginBottom: 4 };
const btn = { padding: "9px 18px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14 };
const btn2 = { padding: "6px 12px", border: "1px solid #0ea5a3", borderRadius: 6, background: "#fff", color: "#0ea5a3", cursor: "pointer", fontSize: 13, fontWeight: 600 };
const badge = { fontSize: 12, fontWeight: 700, padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap" };
const tab = { padding: "4px 10px", border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: 12 };
const tabActive = { ...tab, background: "#0ea5a3", borderColor: "#0ea5a3", color: "#fff", fontWeight: 600 };
const td = { border: "1px solid #e5e7eb", padding: "4px 8px", whiteSpace: "nowrap", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" };

export default HappySchoolFeatureRequests;
